import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DollarSign,
  Users,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  Calendar,
  AlertTriangle,
  Settings,
  CheckSquare,
  Scale,
  BookOpen,
  Star,
  Tag,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminSidebar from '../components/AdminSidebar';
import AdminTabs from '../components/AdminTabs';
import AdminLoadingScreen from '../components/AdminLoadingScreen';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toYMD(date) {
  return date.toISOString().slice(0, 10);
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoMsg, setDemoMsg] = useState(null);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateFrom, setDateFrom] = useState(toYMD(firstOfMonth));
  const [dateTo, setDateTo] = useState(toYMD(today));

  const handleDemo = async (method) => {
    setDemoLoading(true);
    setDemoMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/demo-assets`, {
        method, credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      if (method === 'POST') {
        setDemoMsg({ type: 'success', text: t('admin.demoSeeded', { n: (data.created || []).length }) });
      } else {
        setDemoMsg({ type: 'success', text: t('admin.demoDeleted', { n: data.deleted ?? 0 }) });
      }
      fetchDashboard({ isManual: true });
    } catch (err) {
      setDemoMsg({ type: 'error', text: err.message });
    } finally {
      setDemoLoading(false);
    }
  };

  const statusLabel = (status) => ({
    confirmed: t('admin.statusConfirmed'),
    pending: t('admin.statusPending'),
    completed: t('admin.statusCompleted'),
    cancelled: t('admin.statusCancelled'),
    rejected: t('admin.statusRejected'),
    active: t('admin.statusActive'),
  }[status] || status);

  const fetchDashboard = useCallback(
    async ({ isManual = false } = {}) => {
      if (isManual) setRefreshing(true);
      setError(null);

      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      try {
        const res = await fetch(
          `${BACKEND_URL}/api/admin/dashboard?${params.toString()}`,
          { credentials: 'include' }
        );

        if (res.status === 401) {
          navigate('/login');
          return;
        }

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Error ${res.status}`);
        }

        const data = await res.json();
        setStats(data);
      } catch (err) {
        setError(err.message || t('admin.errorLoadingDesc'));
      } finally {
        setLoading(false);
        if (isManual) setRefreshing(false);
      }
    },
    [dateFrom, dateTo, navigate, t]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    fetchDashboard();
  }, [authLoading, user, navigate, fetchDashboard]);

  useEffect(() => {
    if (authLoading || !user) return;
    const interval = setInterval(() => {
      fetchDashboard();
    }, 30000);
    return () => clearInterval(interval);
  }, [authLoading, user, fetchDashboard]);

  const selectToday = () => {
    const d = toYMD(new Date());
    setDateFrom(d);
    setDateTo(d);
  };

  const selectThisMonth = () => {
    const now = new Date();
    setDateFrom(toYMD(new Date(now.getFullYear(), now.getMonth(), 1)));
    setDateTo(toYMD(now));
  };

  const selectLast30 = () => {
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate() - 30);
    setDateFrom(toYMD(past));
    setDateTo(toYMD(now));
  };

  if (authLoading || loading) {
    return <AdminLoadingScreen skeleton />;
  }

  const navLinks = [
    {
      label: t('admin.verifyLabel'),
      description: t('admin.verifyDesc'),
      path: '/admin/verification',
      Icon: CheckSquare,
      color: 'bg-green-50 text-green-700 border-green-200',
      iconColor: 'bg-green-100 text-green-600',
    },
    {
      label: t('admin.servicesLabel', 'Servicios pendientes'),
      description: t('admin.servicesDesc', 'Aprueba o rechaza servicios publicados'),
      path: '/admin/servicios',
      Icon: CheckSquare,
      color: 'bg-teal-50 text-teal-700 border-teal-200',
      iconColor: 'bg-teal-100 text-teal-600',
    },
    {
      label: t('admin.disputesLabel'),
      description: t('admin.disputesDesc'),
      path: '/admin/disputes',
      Icon: Scale,
      color: 'bg-red-50 text-red-700 border-red-200',
      iconColor: 'bg-red-100 text-red-600',
    },
    {
      label: t('admin.commissionsLabel'),
      description: t('admin.commissionsDesc'),
      path: '/admin/commission',
      Icon: Settings,
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      iconColor: 'bg-blue-100 text-blue-600',
    },
    {
      label: t('admin.reviewsLabel'),
      description: t('admin.reviewsDesc'),
      path: '/admin/reviews',
      Icon: Star,
      color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      iconColor: 'bg-yellow-100 text-yellow-600',
    },
    {
      label: t('admin.categoriesLabel'),
      description: t('admin.categoriesDesc'),
      path: '/admin/categories',
      Icon: Tag,
      color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      iconColor: 'bg-indigo-100 text-indigo-600',
    },
  ];

  const kpiCards = stats
    ? [
        { Icon: DollarSign, label: t('admin.gmvTotal'), value: formatDOP(stats.gmv), color: 'bg-green-100 text-green-600' },
        { Icon: TrendingUp, label: t('admin.commissions'), value: formatDOP(stats.commissions), color: 'bg-blue-100 text-blue-600' },
        { Icon: Users, label: t('admin.totalUsers'), value: stats.total_users ?? '—', color: 'bg-purple-100 text-purple-600' },
        { Icon: AlertCircle, label: t('admin.pending'), value: stats.pending_approvals ?? '—', color: 'bg-yellow-100 text-yellow-600' },
        { Icon: BookOpen, label: t('admin.bookingsThisMonth'), value: stats.bookings_this_month ?? '—', color: 'bg-[#39b4aa]/10 text-[#39b4aa]' },
      ]
    : [];

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1">
        <AdminTabs />

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-4xl font-heading font-bold text-[#134168]">
            {t('admin.adminTitle')}
          </h1>
          <button
            onClick={() => fetchDashboard({ isManual: true })}
            disabled={refreshing}
            className="flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-xl shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-60 font-medium text-sm self-start sm:self-auto"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? t('admin.refreshing') : t('admin.refresh')}
          </button>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">{t('admin.errorLoading')}</p>
              <p className="text-sm mt-0.5">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl p-5 shadow-sm mb-8 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 text-gray-600 font-medium">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{t('admin.filterByDate')}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">{t('admin.from')}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#134168]/30"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 whitespace-nowrap">{t('admin.to')}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#134168]/30"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 sm:ml-auto">
            {[
              { label: t('admin.today'), action: selectToday },
              { label: t('admin.thisMonth'), action: selectThisMonth },
              { label: t('admin.last30'), action: selectLast30 },
            ].map(({ label, action }) => (
              <button
                key={label}
                onClick={action}
                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-[#134168] hover:text-white hover:border-[#134168] transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Panel de vehículos demo — habilita el ciclo completo del cliente */}
        <div className="bg-gradient-to-r from-[#134168] to-[#36C0C7] rounded-2xl p-5 shadow-sm mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-6 h-6 shrink-0 mt-0.5" />
              <div>
                <h2 className="font-bold text-lg">{t('admin.demoTitle')}</h2>
                <p className="text-sm text-white/80">{t('admin.demoDesc')}</p>
                {demoMsg && (
                  <p className={`text-sm mt-2 font-medium ${demoMsg.type === 'error' ? 'text-red-200' : 'text-white'}`}>
                    {demoMsg.text}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => handleDemo('POST')}
                disabled={demoLoading}
                className="bg-white text-[#134168] px-4 py-2 rounded-xl font-bold text-sm hover:bg-white/90 transition disabled:opacity-60"
              >
                {demoLoading ? t('admin.demoWorking') : t('admin.demoPublish')}
              </button>
              <button
                onClick={() => handleDemo('DELETE')}
                disabled={demoLoading}
                className="border border-white/50 text-white px-4 py-2 rounded-xl font-medium text-sm hover:bg-white/10 transition disabled:opacity-60"
              >
                {t('admin.demoRemove')}
              </button>
            </div>
          </div>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
              {kpiCards.map(({ Icon, label, value, color }, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500 truncate">{label}</p>
                      <p className="text-2xl font-bold truncate">{value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {stats.open_disputes > 0 && (
              <div className="mb-8 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <span className="font-semibold">
                    {t('admin.openDisputesShort', { count: stats.open_disputes, n: stats.open_disputes })}
                  </span>
                  <span className="inline-flex items-center justify-center bg-red-600 text-white text-xs font-bold rounded-full w-6 h-6">
                    {stats.open_disputes}
                  </span>
                </div>
                <button
                  onClick={() => navigate('/admin/disputes')}
                  className="ml-auto text-sm font-semibold underline underline-offset-2 hover:text-red-900 transition-colors"
                >
                  {t('admin.viewDisputes')}
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-3xl p-8 shadow-sm">
                <h2 className="text-2xl font-heading font-bold mb-6">{t('admin.statistics')}</h2>
                <div className="space-y-4">
                  {[
                    { label: t('admin.bookingsThisMonth'), value: stats.bookings_this_month },
                    { label: t('admin.totalBookings'), value: stats.total_bookings },
                    { label: t('admin.totalProviders'), value: stats.total_providers },
                    { label: t('admin.activeAssets2'), value: stats.total_assets },
                    {
                      label: t('admin.openDisputes'),
                      value: (
                        <span className={stats.open_disputes > 0 ? 'text-red-600 font-bold' : 'font-bold'}>
                          {stats.open_disputes}
                          {stats.open_disputes > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center bg-red-100 text-red-700 text-xs font-bold rounded-full px-2 py-0.5">!</span>
                          )}
                        </span>
                      ),
                    },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-center border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-600">{label}</span>
                      {typeof value === 'object' ? value : <span className="font-bold">{value ?? '—'}</span>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 shadow-sm">
                <h2 className="text-2xl font-heading font-bold mb-6">{t('admin.latestBookings')}</h2>
                {stats.recent_bookings && stats.recent_bookings.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_bookings.slice(0, 5).map((booking) => (
                      <div key={booking.booking_id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="font-medium text-sm">{booking.booking_code}</p>
                          <p className="text-xs text-gray-500">{new Date(booking.created_at).toLocaleDateString(locale)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm">{formatDOP(booking.total_price)}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700'
                              : booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {statusLabel(booking.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">{t('admin.noRecentBookings')}</p>
                )}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-heading font-bold text-[#134168] mb-4">{t('admin.quickAccess')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {navLinks.map(({ label, description, path, Icon, color, iconColor }) => (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className={`group flex items-center gap-4 bg-white rounded-2xl p-6 shadow-sm border ${color} hover:shadow-md transition-all text-left`}
                  >
                    <div className={`w-12 h-12 ${iconColor} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-base">{label}</p>
                      <p className="text-xs opacity-75 mt-0.5">{description}</p>
                    </div>
                    <span className="ml-auto text-lg opacity-40 group-hover:opacity-80 transition-opacity">→</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
}
